<script setup lang="ts">
import ForecastApi from '@/data/ForecastApi.ts';
import { ForecastedWindow } from '@/data/ForecastedWindow.js';
import { SunriseSunset } from '@/data/SunriseSunset.ts';
import { getSunriseSunset } from '@/data/SunInfo.ts';
import { ref, onMounted } from 'vue';
import type { Ref } from 'vue';
import {addHours, endOfDay, format, isAfter} from 'date-fns';
import { useMetersStore } from '@/stores/meters';
import { MeterDetails } from '@/data/MeterDetails';
import { ForecastApiResult } from '@/data/ForecastApiResult';
import ForecastIcon from '@/components/ForecastIcon.vue';
import ScrollIndicator from '@/components/ScrollIndicator.vue';

let forecastedWindows: Ref<Array<ForecastedWindow>> = ref([]);
let error: Ref<boolean> = ref(false);

onMounted(() => fillForecastedWindows());

function fillForecastedWindows() {
  error.value = false;
  const meterDetails: MeterDetails = useMetersStore().getMeterDetails;

  new ForecastApi()
    .getForecastForToday(meterDetails)
    .then((result: ForecastApiResult) => {
      const hours: Array<ForecastedWindow> =
        mapForecastResultToForecastHours(result);
      const windows: Array<ForecastedWindow> =
        mapForecastHoursToForecastedWindows(hours);
      forecastedWindows.value = fillRatioOfMaxForDay(windows);
    })
    .catch(() => (error.value = true));
}

function mapForecastResultToForecastHours(
  result: ForecastApiResult,
): Array<ForecastedWindow> {
  return result.data.hourly.time.map(
    (time: string, index: number): ForecastedWindow => ({
      time: new Date(time),
      irradianceWm2: result.data.hourly.global_tilted_irradiance[index],
      weatherCode: result.data.hourly.weather_code[index],
    }),
  );
}

function mapForecastHoursToForecastedWindows(
  forecastHours: Array<ForecastedWindow>,
): Array<ForecastedWindow> {
  let forecastedWindows: Array<ForecastedWindow> = [];
  const date = new Date(forecastHours[0].time);
  const sunInfo: SunriseSunset = getSunriseSunset(date);
  const windowSize = 2;
  const hoursInDay = 24;

  for (let i = 0; i < forecastHours.length; i += hoursInDay) {
    const dataForDay = forecastHours.slice(i, i + hoursInDay);
    mapForecastWindowsForDate(
      forecastedWindows,
      dataForDay,
      sunInfo,
      windowSize,
    );

    forecastedWindows.push({
      time: endOfDay(new Date()),
      irradianceWm2: 0,
      weatherCode: 999,
    } as ForecastedWindow);
  }

  return forecastedWindows;
}

function mapForecastWindowsForDate(
  forecastedWindows: Array<ForecastedWindow>,
  data: Array<ForecastedWindow>,
  sunInfo: SunriseSunset,
  windowSize: number,
) {
  const currentDateTime = new Date();

  for (let i = sunInfo.sunriseHour; i < sunInfo.sunsetHour; i += windowSize) {
    const windowHours = data.slice(i, i + windowSize);

    const currentTimeWithMargin = addHours(windowHours[0].time, 1);
    if (isAfter(currentDateTime, currentTimeWithMargin)) {
      continue;
    }

    const sum: number = windowHours.reduce(
      (acc: number, obj: ForecastedWindow) => acc + obj.irradianceWm2,
      0,
    );

    const startTimeOfWindow = windowHours[0].time;
    const maxWeatherCode = Math.max(...windowHours.map((w) => w.weatherCode));

    forecastedWindows.push({
      time: startTimeOfWindow,
      irradianceWm2: Math.ceil(sum),
      weatherCode: maxWeatherCode,
    } as ForecastedWindow);
  }
}

function fillRatioOfMaxForDay(
  forecastedWindows: Array<ForecastedWindow>,
): Array<ForecastedWindow> {
  const irradiances = forecastedWindows.map((fw) => fw.irradianceWm2);
  const maxIrradianceWm2: number = Math.max(...irradiances);

  forecastedWindows.forEach((window: ForecastedWindow) => {
    window.ratioOfMaxForDay = window.irradianceWm2 / maxIrradianceWm2;
  });

  return forecastedWindows;
}
</script>

<template>
  <div>
    <span class="font-lg font-semibold text-slate-500">Zon voorspelling</span>
    <ScrollIndicator :watch-target="forecastedWindows" class="border rounded-xl">
      <div class="flex py-3 pb-3 divide-x">
        <div v-if="forecastedWindows.length < 1" class="flex ml-1">
          <div v-for="i in 6" :key="i">
            <Skeleton height="4rem" width="2.75rem" class="mx-0.5 grid"/>
          </div>
        </div>

        <div v-else-if="error" class="flex flex-col ml-1">
          <div class="flex">
            <div v-for="i in 6" :key="i" class="w-11 h-16 rounded-lg mx-0.5 bg-gradient-to-b from-red-50 to-red-100"/>
          </div>
          <span class="text-sm ml-1 text-red-400">Voorspelling kon niet worden opgehaald</span>
        </div>

        <div v-else class="flex pl-2">
          <div v-for="(window) in forecastedWindows" :key="window.time" class="px-0.5 text-center grid">
            <div
              class="rounded-md p-1.5"
              :class="window.ratioOfMaxForDay > 0.7 ? 'bg-gradient-to-b from-white to-green-200' : ''"
            >
              <forecast-icon :window="window" class="pb-2 w-full"/>
              <span class="font-bold"> {{ format(window.time, 'HH') + 'u' }} </span>
            </div>
          </div>
        </div>
      </div>
    </ScrollIndicator>
  </div>
</template>

<style scoped>

</style>